import usePremiumGate from '../hooks/usePremiumGate';
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Download, Share2, Sparkles, Loader2, Video, Image as ImageIcon, Lock } from 'lucide-react';
import { toast } from 'sonner';
import AudioEffectsManager from '../utils/AudioEffectsManager';
import { isVideoSupported, recordShareVideo } from '../utils/shareVideoRecorder';
import { LS } from '../utils/lang';

/**
 * ShareMomentDialog
 *
 * Renders the current verse + scene label onto a 1080×1080 canvas and lets the
 * user share it via the Web Share API (Instagram / WhatsApp / TikTok / X) or
 * download as PNG. Turns the immersive audio experience into a viral loop.
 */

const THEMES = {
  wilderness: {
    label: 'Wilderness',
    grad: ['#d97706', '#7c2d12'],
    accent: '#fde68a',
  },
  seaside: {
    label: 'Seaside',
    grad: ['#0e7490', '#0f172a'],
    accent: '#7dd3fc',
  },
  garden: {
    label: 'Garden',
    grad: ['#166534', '#052e16'],
    accent: '#bbf7d0',
  },
  temple: {
    label: 'Temple',
    grad: ['#b45309', '#431407'],
    accent: '#fde68a',
  },
  storm: {
    label: 'Storm',
    grad: ['#1e293b', '#020617'],
    accent: '#93c5fd',
  },
  night: {
    label: 'Night',
    grad: ['#1e1b4b', '#020617'],
    accent: '#c4b5fd',
  },
  battle: {
    label: 'Battle',
    grad: ['#7f1d1d', '#111827'],
    accent: '#fca5a5',
  },
  mountain: {
    label: 'Mountain',
    grad: ['#334155', '#0f172a'],
    accent: '#e2e8f0',
  },
  fireside: {
    label: 'Fireside',
    grad: ['#9a3412', '#1c1917'],
    accent: '#fdba74',
  },
  pasture: {
    label: 'Pasture',
    grad: ['#15803d', '#134e4a'],
    accent: '#86efac',
  },
  lumera: {
    label: LS('Lumera (default)', 'Lumera (predeterminado)'),
    grad: ['#7c3aed', '#1e1b4b'],
    accent: '#e9d5ff',
  },
};

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
};

const ShareMomentDialog = ({ isOpen, onClose, verse, initialScene = 'lumera' }) => {
  const canvasRef = useRef(null);
  const effectsManagerRef = useRef(null);
  const [themeKey, setThemeKey] = useState(initialScene in THEMES ? initialScene : 'lumera');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSharing, setIsSharing] = useState(false);

  // Video state
  const [format, setFormat] = useState('image'); // 'image' | 'video'
  const [duration, setDuration] = useState(15); // seconds
  const { isPremium: hasPremiumVoice, requirePremium, dialog: premiumDialog } = usePremiumGate();
  const [withNarration, setWithNarration] = useState(hasPremiumVoice);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);

  const videoSupported = isVideoSupported();

  // Lazy effects manager (needs a user gesture for audio context)
  if (!effectsManagerRef.current && typeof window !== 'undefined') {
    effectsManagerRef.current = new AudioEffectsManager();
  }

  const recordShare = () => {
    if (!verse) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    // Fire-and-forget; leaderboard doesn't block the share flow.
    fetch(`${API}/api/shares/record`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        verse_reference: verse.reference,
        verse_text: verse.text,
        translation: verse.translation || null,
        theme: themeKey,
      }),
    }).catch(() => {
      /* non-critical */
    });
  };

  // Redraw whenever verse or theme changes
  useEffect(() => {
    if (!isOpen || !verse) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const SIZE = 1080;
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    const theme = THEMES[themeKey] || THEMES.lumera;

    // Gradient background
    const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE);
    grad.addColorStop(0, theme.grad[0]);
    grad.addColorStop(1, theme.grad[1]);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Subtle radial glow
    const glow = ctx.createRadialGradient(SIZE * 0.7, SIZE * 0.3, 50, SIZE * 0.7, SIZE * 0.3, SIZE * 0.6);
    glow.addColorStop(0, 'rgba(255,255,255,0.18)');
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Film grain (lightweight)
    ctx.save();
    ctx.globalAlpha = 0.04;
    for (let i = 0; i < 2500; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#fff' : '#000';
      ctx.fillRect(Math.random() * SIZE, Math.random() * SIZE, 1, 1);
    }
    ctx.restore();

    // Lumera branding (top)
    ctx.fillStyle = theme.accent;
    ctx.font = '600 28px -apple-system, "Segoe UI", Roboto, system-ui, sans-serif';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillText('✦ LUMERA', 72, 72);

    // Scene pill (top right)
    const pillLabel = theme.label.toUpperCase();
    ctx.font = '500 22px -apple-system, "Segoe UI", Roboto, system-ui, sans-serif';
    const pillW = ctx.measureText(pillLabel).width + 48;
    const pillH = 44;
    const pillX = SIZE - 72 - pillW;
    const pillY = 72 - 8;
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.beginPath();
    ctx.roundRect(pillX, pillY, pillW, pillH, 22);
    ctx.fill();
    ctx.fillStyle = theme.accent;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(pillLabel, pillX + pillW / 2, pillY + pillH / 2 + 1);

    // Verse text (centered, auto-wrapped, auto-sized)
    const maxWidth = SIZE - 160;
    let fontSize = 64;
    let lines = [];
    while (fontSize > 28) {
      ctx.font = `600 ${fontSize}px "Georgia", "Times New Roman", serif`;
      lines = wrapText(ctx, `"${verse.text}"`, maxWidth);
      const totalH = lines.length * fontSize * 1.25;
      if (totalH <= SIZE * 0.55 && lines.length <= 12) break;
      fontSize -= 4;
    }

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const lineHeight = fontSize * 1.3;
    const totalHeight = lines.length * lineHeight;
    const startY = SIZE / 2 - totalHeight / 2 + lineHeight / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, SIZE / 2, startY + i * lineHeight);
    });

    // Reference
    ctx.font = '600 38px -apple-system, "Segoe UI", Roboto, system-ui, sans-serif';
    ctx.fillStyle = theme.accent;
    ctx.textAlign = 'center';
    ctx.fillText(`— ${verse.reference}`, SIZE / 2, startY + lines.length * lineHeight + 80);

    // Footer tag
    ctx.font = '400 22px -apple-system, "Segoe UI", Roboto, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.textAlign = 'center';
    ctx.fillText('Listen & meditate on Lumera', SIZE / 2, SIZE - 80);

    // Corner hairlines
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 2;
    const c = 40;
    const p = 60;
    ctx.beginPath();
    ctx.moveTo(p, p + c); ctx.lineTo(p, p); ctx.lineTo(p + c, p);
    ctx.moveTo(SIZE - p - c, p); ctx.lineTo(SIZE - p, p); ctx.lineTo(SIZE - p, p + c);
    ctx.moveTo(p, SIZE - p - c); ctx.lineTo(p, SIZE - p); ctx.lineTo(p + c, SIZE - p);
    ctx.moveTo(SIZE - p - c, SIZE - p); ctx.lineTo(SIZE - p, SIZE - p); ctx.lineTo(SIZE - p, SIZE - p - c);
    ctx.stroke();

    // Export preview
    canvas.toBlob((blob) => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (blob) setPreviewUrl(URL.createObjectURL(blob));
    }, 'image/png');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, verse, themeKey]);

  // Release preview url on close
  useEffect(() => {
    if (!isOpen && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const getBlob = () =>
    new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) return resolve(null);
      canvas.toBlob((b) => resolve(b), 'image/png');
    });

  const handleDownload = async () => {
    const blob = await getBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lumera-${(verse?.reference || 'verse').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    recordShare();
    toast.success(LS('Saved to your device', 'Guardado en tu dispositivo'));
  };

  const handleShare = async () => {
    const blob = await getBlob();
    if (!blob) return;
    const file = new File([blob], 'lumera-moment.png', { type: 'image/png' });

    const shareData = {
      title: LS('Lumera — A moment from Scripture', 'Lumera — Un momento de la Escritura'),
      text: `${verse?.text}\n\n— ${verse?.reference}\n\nShared from Lumera`,
      files: [file],
    };

    setIsSharing(true);
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        await navigator.share(shareData);
        recordShare();
        toast.success(LS('Shared!', '¡Compartido!'));
      } else if (navigator.share) {
        // Some browsers support share but not files
        await navigator.share({ title: shareData.title, text: shareData.text });
        recordShare();
        toast.info(LS('Shared text — image saved separately', 'Texto compartido — imagen guardada aparte'));
        handleDownload();
      } else {
        // Fallback: try copying text, then always save the image so user has something to share
        try {
          await navigator.clipboard.writeText(shareData.text);
          toast.success(LS('Verse copied — saving image too', 'Versículo copiado — guardando la imagen también'));
        } catch (_) {
          toast.success(LS('Image saved to your device', 'Imagen guardada en tu dispositivo'));
        }
        handleDownload();
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Share failed:', e);
        toast.error(LS('Share cancelled', 'Compartir cancelado'));
      }
    } finally {
      setIsSharing(false);
    }
  };

  /* ----------------------- Video generation ----------------------- */
  // Resolve ambient layers for the chosen scene (falls back to auto-detect).
  const resolveAmbientLayers = () => {
    const em = effectsManagerRef.current;
    if (!em) return [];
    const scene = em.scenePresets?.[themeKey];
    if (scene?.layers) return scene.layers;
    // Auto-detect from verse text
    return em.analyzeText(verse.text).slice(0, 3);
  };

  // Fetch narration MP3 via our TTS endpoint; null on failure.
  const fetchNarration = async () => {
    if (!withNarration || !hasPremiumVoice) return null;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lumera/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: verse.text, voice: 'nova', model: 'tts-1', speed: 1.0 }),
      });
      if (!res.ok) return null;
      return await res.blob();
    } catch (_) {
      return null;
    }
  };

  const handleGenerateVideo = async () => {
    if (!videoSupported) {
      toast.error(LS('Video export is not supported in this browser', 'Este navegador no permite exportar video'));
      return;
    }
    setIsRendering(true);
    setRenderProgress(0);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
    setVideoBlob(null);
    try {
      const narrationBlob = await fetchNarration();
      const theme = THEMES[themeKey] || THEMES.lumera;
      const blob = await recordShareVideo({
        verse,
        theme,
        durationMs: duration * 1000,
        effectsManager: effectsManagerRef.current,
        ambientLayers: resolveAmbientLayers(),
        narrationBlob,
        onProgress: (p) => setRenderProgress(p),
      });
      const url = URL.createObjectURL(blob);
      setVideoBlob(blob);
      setVideoUrl(url);
      toast.success(LS('Video ready! Preview, download or share it.', '¡Video listo! Míralo, descárgalo o compártelo.'));
    } catch (e) {
      console.error('Video generation failed:', e);
      toast.error(`${LS('Video export failed', 'Falló la exportación del video')}: ${e.message || ''}`);
    } finally {
      setIsRendering(false);
    }
  };

  const handleVideoDownload = () => {
    if (!videoBlob) return;
    const url = URL.createObjectURL(videoBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lumera-${(verse?.reference || 'verse').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    recordShare();
    toast.success(LS('Video saved', 'Video guardado'));
  };

  const handleVideoShare = async () => {
    if (!videoBlob) return;
    const file = new File([videoBlob], 'lumera-moment.webm', { type: videoBlob.type });
    const shareData = {
      title: LS('Lumera — A moment from Scripture', 'Lumera — Un momento de la Escritura'),
      text: `${verse?.text}\n\n— ${verse?.reference}\n\nShared from Lumera`,
      files: [file],
    };
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        await navigator.share(shareData);
        recordShare();
        toast.success(LS('Shared!', '¡Compartido!'));
      } else {
        handleVideoDownload();
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Share failed:', e);
        toast.error(LS('Share cancelled', 'Compartir cancelado'));
      }
    }
  };

  // Cleanup video url when closing
  useEffect(() => {
    if (!isOpen && videoUrl) {
      URL.revokeObjectURL(videoUrl);
      setVideoUrl(null);
      setVideoBlob(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!verse) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="share-moment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {LS('Share this moment', 'Comparte este momento')}
          </DialogTitle>
          <DialogDescription>
            {LS('Generate a branded verse card to post on Instagram, WhatsApp, X, and more.', 'Crea una tarjeta del versículo para publicar en Instagram, WhatsApp, X y más.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Format tabs */}
          <Tabs value={format} onValueChange={setFormat}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="image" data-testid="share-format-image">
                <ImageIcon className="w-4 h-4 mr-2" />
                {LS('Image', 'Imagen')}
              </TabsTrigger>
              <TabsTrigger value="video" data-testid="share-format-video" disabled={!videoSupported}>
                <Video className="w-4 h-4 mr-2" />
                {LS('Video', 'Video')}
                {!videoSupported && <Lock className="w-3 h-3 ml-1" />}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Preview */}
          <div
            className={`${
              format === 'video' ? 'aspect-[9/16] max-h-[520px] mx-auto' : 'aspect-square w-full'
            } rounded-lg overflow-hidden border border-border bg-muted flex items-center justify-center`}
          >
            {format === 'image' ? (
              previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Verse share card preview"
                  className="w-full h-full object-contain"
                  data-testid="share-preview-img"
                />
              ) : (
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              )
            ) : videoUrl ? (
              <video
                src={videoUrl}
                controls
                className="w-full h-full object-contain"
                data-testid="share-preview-video"
              />
            ) : isRendering ? (
              <div className="flex flex-col items-center gap-3 p-6 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  Rendering video… {Math.round(renderProgress * 100)}%
                </p>
                <div className="w-48 h-1.5 bg-muted-foreground/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${renderProgress * 100}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 p-6 text-center text-muted-foreground">
                <Video className="w-10 h-10" />
                <p className="text-sm">Generate a {duration}s vertical video clip with narration &amp; ambience</p>
              </div>
            )}
          </div>

          {/* Hidden canvas used for image export */}
          <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

          {/* Theme picker */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{LS('Scene', 'Escena')}</span>
            <Select value={themeKey} onValueChange={setThemeKey}>
              <SelectTrigger className="w-48" data-testid="share-theme-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(THEMES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Video-only settings */}
          {format === 'video' && (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{LS('Duration', 'Duración')}</span>
                <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v, 10))}>
                  <SelectTrigger className="w-32" data-testid="share-video-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 seconds</SelectItem>
                    <SelectItem value="15">15 seconds</SelectItem>
                    <SelectItem value="20">20 seconds</SelectItem>
                    <SelectItem value="30">30 seconds</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium flex items-center gap-1.5">
                  {LS('Include narration', 'Incluir narración')}
                  {!hasPremiumVoice && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Lumera+</span>}
                </span>
                <Button
                  variant={withNarration && hasPremiumVoice ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { if (!requirePremium('AI voice narration')) return; setWithNarration((v) => !v); }}
                  data-testid="share-video-narration-toggle"
                >
                  {withNarration && hasPremiumVoice ? 'On' : 'Off'}
                </Button>
                {premiumDialog}
              </div>
            </>
          )}

          {/* Actions */}
          {format === 'image' ? (
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleDownload}
                variant="outline"
                className="w-full"
                data-testid="share-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {LS('Download', 'Descargar')}
              </Button>
              <Button
                onClick={handleShare}
                className="w-full"
                disabled={isSharing}
                data-testid="share-send-btn"
              >
                {isSharing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4 mr-2" />
                )}
                {LS('Share', 'Compartir')}
              </Button>
            </div>
          ) : !videoUrl ? (
            <Button
              onClick={handleGenerateVideo}
              className="w-full"
              disabled={isRendering || !videoSupported}
              data-testid="share-video-generate-btn"
            >
              {isRendering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rendering… {Math.round(renderProgress * 100)}%
                </>
              ) : (
                <>
                  <Video className="w-4 h-4 mr-2" />
                  Generate {duration}s video
                </>
              )}
            </Button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleVideoDownload}
                variant="outline"
                className="w-full"
                data-testid="share-video-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {LS('Download', 'Descargar')}
              </Button>
              <Button
                onClick={handleVideoShare}
                className="w-full"
                data-testid="share-video-send-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {LS('Share', 'Compartir')}
              </Button>
            </div>
          )}

          <p className="text-xs text-center text-muted-foreground">
            {format === 'image'
              ? LS('Posts to Instagram, WhatsApp, X, and more via your device\u2019s share sheet.', 'Publica en Instagram, WhatsApp, X y más desde el menú de compartir.')
              : videoSupported
              ? LS('Vertical 9:16 WebM for Reels, TikTok, Shorts & Stories.', 'WebM vertical 9:16 para Reels, TikTok, Shorts e Historias.')
              : LS('Video export requires a Chromium-based or modern browser.', 'Exportar video requiere un navegador moderno.')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareMomentDialog;
